/**
 * SSEContext — SATU koneksi Server-Sent Events untuk seluruh aplikasi.
 *
 * Kenapa harus satu:
 *   Dulu tiap halaman bikin EventSource sendiri lewat hooks/useSSE. Di POS itu
 *   aman (halaman unmount saat pindah), tapi di admin TIDAK: tab admin keep-alive
 *   (lihat pages/admin/KeepAliveOutlet.jsx) — 8 tab terbuka = 8 halaman tetap
 *   mounted = 8 koneksi SSE hidup bersamaan. Browser cuma mengizinkan ±6 koneksi
 *   per domain di HTTP/1.1, dan koneksi SSE tidak pernah menutup. Tab ke-7 bikin
 *   SELURUH request API menggantung tanpa pesan error apa pun.
 *
 *   Sekarang: satu koneksi, halaman cuma numpang lewat subscribe().
 *
 * Koneksi TIDAK ditutup saat tab browser disembunyikan (dulu ditutup demi hemat).
 * Justru itu yang bikin data tetap masuk saat user pindah ke aplikasi lain, dan
 * biayanya sekarang cuma satu koneksi.
 *
 * Kalau koneksi sempat putus lalu nyambung lagi, event yang lewat selama putus
 * hilang selamanya — server tidak menyimpan riwayat. Karena itu setiap
 * penyambungan ulang menyiarkan { type: 'sse_reconnect' } supaya halaman memuat
 * ulang datanya sendiri dan tidak diam-diam menampilkan angka basi.
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { API_URL as BASE_URL } from '../utils/api';

export const SSEContext = createContext(null);
export const useSSEBus = () => useContext(SSEContext);

const RETRY_MS = 5000;

function getToken() {
  return localStorage.getItem('ft_token') || sessionStorage.getItem('ft_session_token') || '';
}

export function SSEProvider({ children }) {
  const { user } = useAuth();
  const userId = user?._id || user?.id || null;

  const subsRef = useRef(new Set());

  // Objek bus dibuat sekali dan tidak pernah berganti identitas → useEffect di
  // pelanggan (hooks/useSSE) tidak ikut jalan ulang tiap provider re-render.
  const busRef = useRef(null);
  if (!busRef.current) {
    busRef.current = {
      subscribe(fn) {
        subsRef.current.add(fn);
        return () => subsRef.current.delete(fn);
      },
    };
  }

  useEffect(() => {
    if (!userId || !window.EventSource) return undefined;

    let es = null;
    let retryTimer = null;
    let destroyed = false;
    let everOpened = false; // sudah pernah tersambung?
    let sawDrop = false; // sempat putus setelah tersambung?

    const emit = (payload) => {
      // Salin dulu: handler boleh unsubscribe dari dalam dirinya sendiri.
      for (const fn of [...subsRef.current]) {
        try {
          fn(payload);
        } catch (err) {
          console.error('[SSE] handler error:', err);
        }
      }
    };

    function connect() {
      if (destroyed || es) return;
      const token = getToken();
      if (!token) return;

      es = new EventSource(`${BASE_URL}/events?token=${encodeURIComponent(token)}`);

      es.onopen = () => {
        if (everOpened && sawDrop) emit({ type: 'sse_reconnect' }); // ada jeda → muat ulang
        everOpened = true;
        sawDrop = false;
      };

      es.onmessage = (e) => {
        if (!e.data || e.data.trim() === '') return; // heartbeat
        try {
          emit(JSON.parse(e.data));
        } catch {
          // bukan JSON, abaikan
        }
      };

      es.onerror = () => {
        sawDrop = true;
        es?.close();
        es = null;
        if (!destroyed) {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(connect, RETRY_MS);
        }
      };
    }

    // Balik ke tab / internet nyambung lagi → jangan tunggu sisa jeda retry.
    function reconnectNow() {
      if (destroyed || document.hidden) return;
      if (!es) {
        clearTimeout(retryTimer);
        connect();
      }
    }

    connect();
    document.addEventListener('visibilitychange', reconnectNow);
    window.addEventListener('online', reconnectNow);

    return () => {
      destroyed = true;
      clearTimeout(retryTimer);
      es?.close();
      document.removeEventListener('visibilitychange', reconnectNow);
      window.removeEventListener('online', reconnectNow);
    };
  }, [userId]);

  return <SSEContext.Provider value={busRef.current}>{children}</SSEContext.Provider>;
}

export default SSEProvider;
